import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  CircularProgress,
} from '@mui/material';
import { userLanguageService } from '../../services/userLanguageService';
import { SUPPORTED_LANGUAGES } from '../../constants/languages';

interface Props {
  languages: string[];
  onLanguagesChange: (languages: string[]) => void;
}

const LanguagesSection: React.FC<Props> = ({ languages, onLanguagesChange }) => {
  const [selected, setSelected] = useState<string[]>(languages);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const isDirty = JSON.stringify(selected) !== JSON.stringify(languages);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await userLanguageService.setLanguages(selected);
      onLanguagesChange(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save languages');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Languages Spoken</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Languages saved.</Alert>}

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Languages</InputLabel>
        <Select
          multiple
          value={selected}
          onChange={e => setSelected(e.target.value as string[])}
          input={<OutlinedInput label="Languages" />}
          renderValue={sel => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(sel as string[]).map(v => <Chip key={v} label={v} size="small" />)}
            </Box>
          )}
        >
          {SUPPORTED_LANGUAGES.map(lang => (
            <MenuItem key={lang} value={lang}>{lang}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || !isDirty}
        endIcon={saving ? <CircularProgress size={16} /> : null}
      >
        {saving ? 'Saving…' : 'Save Languages'}
      </Button>
    </Box>
  );
};

export default LanguagesSection;
